import { FC, useState, useEffect, useMemo } from "react";
import { ScreeningTemplate } from "./ScreeningTemplate";
import { TemplateButton } from "./TemplateButton";
import { CreateTemplateModal } from "../../../shared/app/VerificationTemplate/modals/CreateTemplateModal";
import {
  Template,
  VerificationSchema
} from "types/landlord/verificationTemplate";

import templatesApi from "api/landlord/templates";
import { getErrorMessage } from "utils/error";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { Loader } from "shared/components/Loader";
import {
  onSaveCleanUp,
  withoutTemplateRouting,
  withoutUnderwritingAgents
} from "shared/app/VerificationTemplate/helpers";
import { useAuthContext } from "providers/Auth";
import { FEATURES } from "types/landlord/partner";

interface ScreeningSettingsProps {
  templates: Template[];
  verificationSchemas: VerificationSchema[];
  defaultTemplate: Template;
  addTemplate: (template: Template) => void;
  removeTemplateById: (id: string) => void;
  isLoading: boolean;
}

export const ScreeningSettings: FC<ScreeningSettingsProps> = ({
  templates,
  isLoading,
  verificationSchemas,
  defaultTemplate,
  addTemplate,
  removeTemplateById
}) => {
  const [isPopupOpen, setPopupOpen] = useState(false);
  const [template, setTemplate] = useState<Template>({
    ...withoutTemplateRouting(defaultTemplate),
    name: ""
  });

  const location = useLocation();
  const { partner } = useAuthContext();

  useEffect(() => {
    if (location.search.includes("create-mode=true")) setPopupOpen(true);
  }, []);

  const createTemplate = async (newTemplate: Template) => {
    try {
      let updatedTemplate: Template =
        onSaveCleanUp.onlyNewIncomePresent(newTemplate);
      updatedTemplate = onSaveCleanUp.filterOutHousingHistory(updatedTemplate);
      updatedTemplate = onSaveCleanUp.disableBoomFinanceIfFeatureAbsent(
        updatedTemplate,
        Boolean(partner?.features.includes(FEATURES.WIP_BOOM_FINANCE))
      );
      updatedTemplate =
        onSaveCleanUp.disableBoomFinanceForGuarantors(updatedTemplate);

      updatedTemplate = onSaveCleanUp.petPolicy(updatedTemplate);
      // The default template's agent switch would be refused without the
      // agent's id; the agent is picked on the new template's page instead.
      updatedTemplate = withoutUnderwritingAgents(updatedTemplate);

      const data = await templatesApi.createTemplate(updatedTemplate);
      toast.success("New template successfully created");
      setPopupOpen(false);
      addTemplate(data.verification_template);
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      toast.error(errorMessage);
    }
  };

  const handleTemplateUpdate = (updatedTemplate: Template) => {
    setTemplate(updatedTemplate);
  };

  useEffect(() => {
    setTemplate({ ...withoutTemplateRouting(defaultTemplate), name: "" });
  }, [defaultTemplate]);

  const sortedTemplates = useMemo(() => {
    return [...templates].sort(
      (a, b) => (b.default ? 1 : 0) - (a.default ? 1 : 0)
    );
  }, [templates]);

  return (
    <div className="flex w-full flex-wrap gap-4">
      <TemplateButton onClick={() => setPopupOpen(!isPopupOpen)} />
      {isLoading ? (
        <div className="px-10 min-h-[200px] flex items-center justify-center">
          <Loader />
        </div>
      ) : (
        sortedTemplates.map((template: Template) => {
          const updatedTemplate = {
            ...template,
            settings: {
              ...template.settings,
              applicant: {
                ...template.settings.applicant,
                enabled_screening_services: true
              },
              co_signer: {
                ...template.settings.co_signer,
                enabled_screening_services:
                  template.settings.co_signer.enabled_screening_services ?? true
              }
            }
          };
          return (
            <ScreeningTemplate
              key={template.id}
              template={updatedTemplate}
              addTemplate={addTemplate}
              removeTemplateById={removeTemplateById}
              verificationSchemas={verificationSchemas}
            />
          );
        })
      )}
      {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
      {template && (
        <CreateTemplateModal
          opened={isPopupOpen}
          template={template}
          defaultTemplate={defaultTemplate}
          verificationSchemas={verificationSchemas}
          handleCancel={() => setPopupOpen(false)}
          handleSave={createTemplate}
          onTemplateUpdate={handleTemplateUpdate}
        />
      )}
    </div>
  );
};
